<template>
  <div class="car-create-fee-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('carCreateFee.vehicleCharging')" :subtitle="$t('carCreateFee.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-plus" @click="handleOpenFeeImportExcel">
          {{ $t('carCreateFee.customTemplate') }}
        </el-button>
        <el-button icon="el-icon-plus" @click="handleOpenDoCreateRoomFee">
          {{ $t('carCreateFee.customCreate') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="handleBatchCreate">
          {{ $t('carCreateFee.batchCreate') }}
        </el-button>
        <el-button type="primary" icon="el-icon-shopping-card-full" @click="handleBuyMonthlyCard">
          {{ $t('carCreateFee.buyMonthlyCard') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="queryParams.allNum" clearable class="w-full"
            :placeholder="$t('carCreateFee.parkingSpacePlaceholder')" @keyup.enter.native="handleQuery" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="queryParams.carNumLike" clearable class="w-full"
            :placeholder="$t('carCreateFee.carNumPlaceholder')" @keyup.enter.native="handleQuery" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="queryParams.ownerName" clearable class="w-full"
            :placeholder="$t('carCreateFee.ownerNamePlaceholder')" @keyup.enter.native="handleQuery" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="queryParams.state" clearable class="w-full"
            :placeholder="$t('carCreateFee.parkingSpaceStatus')">
            <el-option v-for="item in states" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleQuery">
          {{ $t('carCreateFee.query') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('carCreateFee.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="fetchData">
        <template #left>
          <span class="ds-panel__title">{{ $t('carCreateFee.vehicleCharging') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="cars" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('carCreateFee.page.empty')" @retry="fetchData">
        <el-table-column prop="carNum" :label="$t('carCreateFee.licensePlate')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.carNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="areaNum" :label="$t('carCreateFee.parkingLot')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.areaNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="num" :label="$t('carCreateFee.parkingSpace')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.num || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="ownerName" :label="$t('carCreateFee.ownerName')" align="left" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.ownerName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="link" :label="$t('carCreateFee.contact')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.link || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('carCreateFee.status')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag type="info" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('carCreateFee.operation')" align="center" fixed="right" width="130">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleViewCharges(scope.row)">
              {{ $t('carCreateFee.viewCharges') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('carCreateFee.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <car-create-fee-add ref="carCreateFeeAdd" @success="handleSuccess" />
    <export-car-fee-import-excel ref="exportCarFeeImportExcel" />
    <do-import-create-fee ref="doImportCreateFee" />
  </div>
</template>

<script>
import { listCarCreateFees } from '@/api/fee/carCreateFeeApi'
import { getDict } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CarCreateFeeList',
  components: {
    'car-create-fee-add': () => import('@/components/fee/carCreateFeeAdd'),
    'export-car-fee-import-excel': () => import('@/components/fee/exportCarFeeImportExcel'),
    'do-import-create-fee': () => import('@/components/fee/doImportCreateFee'),
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      queryParams: {
        allNum: '',
        carNumLike: '',
        ownerName: '',
        state: '',
        carTypeCd: '1001',
        page: 1,
        row: 10
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      cars: [],
      states: []
    }
  },
  created() {
    this.getDictData()
    this.fetchData()
  },
  methods: {
    async getDictData() {
      try {
        this.states = await getDict('owner_car', 'state')
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async fetchData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const res = await listCarCreateFees(this.queryParams)
        this.cars = Array.isArray(res.data) ? res.data : []
        this.pagination.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('carCreateFee.page.loadFailed')
        this.$message.error(this.$t('common.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleQuery() {
      this.queryParams.page = 1
      this.pagination.current = 1
      this.fetchData()
    },
    handleReset() {
      this.queryParams = {
        allNum: '',
        carNumLike: '',
        ownerName: '',
        state: '',
        carTypeCd: '1001',
        page: 1,
        row: this.queryParams.row
      }
      this.pagination.current = 1
      this.fetchData()
    },
    handleSizeChange(size) {
      this.queryParams.row = size
      this.pagination.size = size
      this.fetchData()
    },
    handleCurrentChange(current) {
      this.queryParams.page = current
      this.pagination.current = current
      this.fetchData()
    },
    handleBatchCreate() {
      this.$refs.carCreateFeeAdd.open({ isMore: true })
    },
    handleOpenFeeImportExcel() {
      this.$refs.exportCarFeeImportExcel.open()
    },
    handleOpenDoCreateRoomFee() {
      this.$refs.doImportCreateFee.open()
    },
    handleBuyMonthlyCard() {
      this.$router.push({ path: '/views/fee/buyCarMonthCard' })
    },
    handleViewCharges(row) {
      this.$router.push({
        path: '/views/fee/listCarFee',
        query: {
          carId: row.carId,
          carNum: row.carNum,
          areaNum: row.areaNum,
          num: row.num
        }
      })
    },
    handleSuccess() {
      this.fetchData()
    }
  }
}
</script>

<style lang="scss" scoped>
.car-create-fee-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
